// src/components/admin/PaymentStatusPill.tsx
import React from "react";

type Props = {
  /** requested/approved/complete/failed/rejected... 또는 "승인대기/결제대기/..." */
  status?: string | null;
  className?: string;
};

type Label =
  | "승인대기"
  | "결제대기"
  | "결제완료"
  | "송금완료"
  | "요청철회/거절"
  | "결제실패"
  | "진행중";

function normalize(s?: string | null) {
  return String(s || "").trim();
}

function toLabel(raw?: string | null): Label {
  const s = normalize(raw);
  if (!s) return "진행중";

  // 1) 이미 한글 라벨
  if (
    s === "승인대기" ||
    s === "결제대기" ||
    s === "결제완료" ||
    s === "송금완료" ||
    s === "요청철회/거절" ||
    s === "결제실패"
  ) {
    return s;
  }

  // 2) 영문/내부 상태
  const v = s.toLowerCase();

  if (["pending", "request", "wait", "waiting", "requested"].includes(v))
    return "승인대기";

  if (["approved", "ready", "pay_wait", "pay_ready", "waiting_for_payment"].includes(v))
    return "결제대기";

  if (["paid", "complete", "completed", "done", "success"].includes(v))
    return "결제완료";

  if (["remitted", "sent", "transfer_done", "remit_complete"].includes(v))
    return "송금완료";

  if (["rejected", "denied", "canceled", "cancelled", "withdrawn"].includes(v))
    return "요청철회/거절";

  if (["failed", "fail", "error"].includes(v)) return "결제실패";

  return "진행중";
}

function styleFor(label: Label) {
  switch (label) {
    case "승인대기":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "결제대기":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "결제완료":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "송금완료":
      return "bg-sky-50 text-sky-700 border-sky-200"; // ✅ 결제완료와 색상 분리
    case "요청철회/거절":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "결제실패":
      return "bg-rose-50 text-rose-800 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

export default function PaymentStatusPill({ status, className }: Props) {
  const raw = normalize(status);
  const label = toLabel(raw);
  const style = styleFor(label);

  // ✅ 길이가 긴 라벨은 조금 더 컴팩트하게
  const compact =
    label === "요청철회/거절" ? "px-2.5 py-1 text-[10.5px]" : "px-3 py-1 text-[11px]";

  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded-full border font-semibold",
        "leading-none whitespace-nowrap", // ✅ 줄바꿈 방지 + 높이 안정화
        compact,
        style,
        className || "",
      ].join(" ")}
      title={label}
    >
      {label}
    </span>
  );
}
